import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import {
  getGamePackages,
  GetGamePackagesRequest,
  GamePackageData,
} from '@/application/requests/gamePackage';

export interface PackageState {
  packages: GamePackageData[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

const initialState: PackageState = {
  packages: [],
  loading: false,
  error: null,
  currentPage: 1,
  totalPages: 1,
  totalItems: 0,
  pageSize: 10,
  hasNext: false,
  hasPrevious: false,
};

export const fetchPackages = createAsyncThunk(
  'package/fetchPackages',
  async (params: GetGamePackagesRequest, { rejectWithValue }) => {
    try {
      const response = await getGamePackages(params);

      if (!response.success) {
        return rejectWithValue(response.message || 'Failed to fetch packages');
      }

      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch packages');
    }
  }
);

const packageSlice = createSlice({
  name: 'package',
  initialState,
  reducers: {
    setCurrentPage: (state, action: PayloadAction<number>) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action: PayloadAction<number>) => {
      state.pageSize = action.payload;
      state.currentPage = 1; // Reset to first page when changing page size
    },
    clearError: (state) => {
      state.error = null;
    },
    resetPackages: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchPackages.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPackages.fulfilled, (state, action) => {
        state.loading = false;
        if (action.payload) {
          state.packages = action.payload.data;
          state.totalPages = action.payload.total_pages;
          state.totalItems = action.payload.total;
          state.currentPage = action.payload.page;
          state.pageSize = action.payload.limit;
          state.hasNext = action.payload.has_next;
          state.hasPrevious = action.payload.has_previous;
        }
      })
      .addCase(fetchPackages.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setCurrentPage, setPageSize, clearError, resetPackages } =
  packageSlice.actions;

export default packageSlice.reducer;
